import React, { useEffect, useState, useCallback, useRef, FC } from 'react';
import AriaModal from 'react-aria-modal';
import Departments from 'models/departments';
import { useAppContext } from '../../../store/appContext';
import DepartmentBox from '../../DepartmentBox/DepartmentBox';
import NavigationLevel from './NavigationLevel';
import ActionLinks from '../../Header/ActionLinks/ActionLinks';
import { categorySelectedEvent } from '../../../common/models/TrackingEvents';
import { track } from '../../../helpers/tracking/tracking';
import { useDelayUnmount } from '../../../hooks/useDelayUnmount';

const NavigationMenuMobile: FC<{
  changeShippingVisibility: () => void;
  department: string;
  getDepartmentLink: (department: string) => string;
  handleClickDepartment: (selectedDepartment: string) => void;
  initialDepartment?: string;
  isOpen: boolean;
  toggle: () => void;
}> = ({
  changeShippingVisibility,
  isOpen,
  toggle,
  initialDepartment,
  handleClickDepartment,
  department,
  getDepartmentLink,
}) => {
  const {
    country,
    menu: { menu },
  } = useAppContext();
  const [navigationState, setNavigationState] = useState({
    dataStack: [menu.find(el => el.name.toLowerCase() === Departments.getInternalName(department))],
  });
  const [resetDepartment, setResetDepartment] = useState(false);
  const contentRef = useRef<HTMLDivElement>();

  const delayedIsOpen = useDelayUnmount(isOpen, 300);

  const handleLevel = (curr, { allCategoryClick = false } = {}) => {
    const data = {
      ...curr,
    };

    if (allCategoryClick || (curr.children && curr.children.length === 0)) {
      const isDesigner = curr.name.toLowerCase() === 'designers';
      const category = isDesigner ? 'Designers' : navigationState.dataStack[navigationState.dataStack.length - 1].name;
      const subcategory = !allCategoryClick || isDesigner ? curr.name : `All ${curr.name}`;
      const payload = { categoryDepartment: { category, department: department.toLowerCase(), subcategory } };
      track(categorySelectedEvent, payload);
      return;
    }

    const newNavigationState = { ...navigationState };
    newNavigationState.dataStack.push(data);
    setNavigationState(newNavigationState);
  };

  const handleBack = useCallback(() => {
    const newNavigationState = { ...navigationState };
    newNavigationState.dataStack.pop();
    setNavigationState(newNavigationState);
  }, [navigationState]);

  const resetNavigationLevel = (menuArr, dep) => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
    setNavigationState({
      dataStack: [menuArr.find(el => el.name.toLowerCase() === Departments.getInternalName(dep))],
    });
  };

  useEffect(() => {
    resetNavigationLevel(menu, department);
  }, [department, menu]);

  useEffect(() => {
    if (isOpen) {
      setResetDepartment(false);
    }
    resetNavigationLevel(menu, department);
  }, [department, isOpen, menu]);

  return (
    <>
      {delayedIsOpen && (
        <AriaModal
          dialogStyle={{ height: '100%', pointerEvents: 'none', width: '100%' }}
          onExit={() => {
            resetNavigationLevel(menu, department);
            setResetDepartment(true);
            toggle();
          }}
          titleText="Navigation Menu"
          underlayClass={isOpen ? 'after-open' : 'before-close'}
          underlayClickExits
          underlayColor="rgb(0, 0, 0, 0.01)"
        >
          <div className="navigation-menu">
            <div className="header">
              <DepartmentBox
                context="nav"
                department={department}
                getDepartmentLink={getDepartmentLink}
                handleClick={(selectedDepartment, e) => {
                  e?.preventDefault();
                  if (contentRef.current) {
                    contentRef.current.scrollTop = 0;
                  }
                  handleClickDepartment(selectedDepartment);
                }}
                initialDepartment={initialDepartment}
                resetDepartment={resetDepartment}
                underline
              />
            </div>

            <div ref={contentRef} className="content">
              <NavigationLevel
                handleBack={handleBack}
                handleLevel={handleLevel}
                level={navigationState.dataStack.length - 1}
                values={navigationState.dataStack[navigationState.dataStack.length - 1]}
              />

              {navigationState.dataStack.length === 1 && (
                <>
                  <div className="auth-btns-container">
                    <ActionLinks />
                  </div>

                  <div className="change-shipping-column">
                    <span className="change-shippng-label">You are shipping to</span>
                    <span
                      className="change-shipping-country"
                      data-cy="footer-shipping-country-label"
                      onClick={() => {
                        resetNavigationLevel(menu, department);
                        setResetDepartment(true);
                        toggle();
                        changeShippingVisibility();
                      }}
                    >
                      {country}
                    </span>
                  </div>
                </>
              )}
            </div>

            <style jsx>{`
              .auth-btns-container {
                display: flex;
                flex-direction: column;
                margin-bottom: 10px;
                margin-top: 38px;
              }

              .change-shipping-column {
                display: flex;
                flex-direction: column;
                margin-bottom: 40px;
              }

              .change-shippng-label {
                color: #222222;
                text-align: center;
                margin-bottom: 5px;
              }

              .change-shipping-country {
                text-decoration: underline;
                cursor: pointer;
                color: #222222;
                text-align: center;
              }

              .navigation-menu {
                background: #fff;
                height: 100%;
                overflow: hidden;
                display: flex;
                flex-direction: column;
                width: 75%;
                max-width: 550px;
                pointer-events: auto;
              }

              .header {
                width: 100%;
                padding: 20px 0;
                z-index: 1;
                display: flex;
                justify-content: space-around;
              }

              .content {
                height: 100%;
                width: 100%;
                overflow-y: scroll;
                -webkit-overflow-scrolling: touch;
              }

              :global(.after-open) {
                animation: slideIn 0.3s forwards;
                z-index: 1000002 !important;
              }

              :global(.before-close) {
                animation: slideOut 0.3s forwards;
              }

              @keyframes slideIn {
                0% {
                  transform: translateX(-100%);
                }
                100% {
                  transform: translateX(0);
                }
              }
              @keyframes slideOut {
                0% {
                  transform: translateX(0);
                }
                100% {
                  transform: translateX(-100%);
                }
              }
            `}</style>
          </div>
        </AriaModal>
      )}
      {!delayedIsOpen && (
        <>
          <div className="navigation-menu">
            <DepartmentBox
              context="nav"
              department={department}
              getDepartmentLink={getDepartmentLink}
              underline={false}
            />
            {menu.map(subMenu => (
              <>
                <NavigationLevel level={0} values={subMenu} />
                {subMenu.children.map(subsubmenu => (
                  <NavigationLevel level={1} values={subsubmenu} />
                ))}
              </>
            ))}
          </div>

          <style jsx>
            {`
              .navigation-menu {
                height: 0;
                overflow: hidden;
                visibility: hidden;
              }
            `}
          </style>
        </>
      )}
    </>
  );
};

export default NavigationMenuMobile;
